import { ReactElement, ReactNode } from 'react';
import { Pressable, ViewProps } from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { IDataAttributes } from '@/common/types/general';
import { styles } from './Alert.styles';

export const Alert = ({
  className,
  type = 'info',
  children,
  onClose,
  noBackground = false,
  ...rest
}: TAlertProps) => {
  const icon = getIcon({ type, noBackground });

  return (
    <Stack
      horizontal
      className={styles.root({ type, noBackground, className })}
      {...rest}
    >
      <Stack
        align="center"
        justify="center"
        className={styles.icon({ type, noBackground })}
      >
        {icon}
      </Stack>
      <Stack horizontal justify="between" className={styles.contentContainer}>
        <BodyText
          className={styles.content({ type, noBackground })}
          context="provider"
        >
          {children}
        </BodyText>
        {onClose && (
          <Pressable
            className={styles.contentCloseButton}
            onPress={() => onClose()}
          >
            <Icon icon="xmark" color="neutral30" />
          </Pressable>
        )}
      </Stack>
    </Stack>
  );
};

Alert.displayName = 'Alert';

const getIcon = ({
  type,
  noBackground,
}: Required<Pick<TAlertProps, 'type' | 'noBackground'>>): ReactElement => {
  let map: Record<string, ReactElement> = {
    info: <Icon icon="info" color="neutral10" />,
    success: <Icon icon="check" color="neutral10" />,
    warning: <Icon icon="exclamation" color="neutral10" />,
    error: <Icon icon="xmark" color="neutral10" />,
  };

  if (noBackground) {
    map = {
      info: <Icon icon="circle-info" size={20} color="systemInfo" />,
      success: <Icon icon="circle-check" size={20} color="systemSuccess" />,
      warning: (
        <Icon icon="circle-exclamation" size={20} color="systemWarning" />
      ),
      error: <Icon icon="circle-xmark" size={20} color="systemError" />,
    };
  }

  return map[type];
};

export type TAlertProps = ViewProps &
  IDataAttributes & {
    children?: ReactNode;
    className?: string;
    onClose?: Function;
    type?: 'success' | 'warning' | 'error' | 'info';
    noBackground?: boolean;
  };
